<script lang="ts">
	// The design system's NameTile (design/components/core/NameTile.jsx): one
	// person the till can sign in, on the lock screen. A tap picks them, and
	// the passcode entry follows.
	//
	// It renders its own list item holding a real button, so the caller puts
	// the tiles in a <ul role="list"> and a screen reader hears a list of
	// buttons. The press inverts, like a key or a menu tile, because a name is
	// tapped fast and often without looking. The height is fixed (--name-h)
	// and a long name stops at two lines, so nothing shifts the grid under the
	// next tap; the button's text is still the whole name.
	//
	// There is no disabled state: someone who cannot sign in here is not in
	// the list at all.
	//
	// With `phone` the same tile is a row: a saved customer suggested while a
	// phone is typed (T7). The name and the number share line one, the last
	// address and its area make line two, each one line with an ellipsis, in
	// the same box. `match` is the digits typed, set strong in the number so
	// the cashier sees why the row is there. Rows go in one column.
	//
	// The plain name is centred, not at the start as in the design: a
	// divergence the user allowed (2026-09-27). A row keeps the design's start.
	//
	// With `chosen` the tiles are one choice, as the Till page draws Pick a
	// table (T15): the chosen one holds the design's pressed face, the
	// inverted ground (data-pressed), with a check before its name, and every
	// tile says whether it is the one picked. The design system's NameTile
	// has no such state: a divergence the owner allowed (2026-10-12, #345),
	// until the design project draws one (docs/backlog.md).

	import Icon from './Icon.svelte';
	import { pressable } from './pressable';

	interface Props {
		testId: string;
		name: string;
		onPick: () => void;
		/** Makes the tile a row, with the number beside the name. */
		phone?: string;
		/** The row's second line. */
		detail?: string;
		/** The digits typed, shown strong where they fall in the number. */
		match?: string;
		/**
		 * One tile of a set that picks one: true for the one picked, false for
		 * the rest. Left out, the tile only picks, as on the lock screen.
		 */
		chosen?: boolean;
	}

	let { testId, name, onPick, phone, detail, match, chosen }: Props = $props();

	const row = $derived(phone !== undefined || detail !== undefined);
	const at = $derived(phone && match ? phone.indexOf(match) : -1);
</script>

<li class="item">
	<button
		type="button"
		class="tile"
		class:row
		data-testid={testId}
		data-press="invert"
		data-pressed={chosen || undefined}
		aria-pressed={chosen}
		onclick={onPick}
		{@attach pressable}
	>
		{#if row}
			<span class="first">
				<span class="line rowName">{name}</span>
				{#if phone !== undefined}
					<bdi class="phone" dir="ltr"
						>{#if at < 0}{phone}{:else}{phone.slice(0, at)}<span class="match"
								>{phone.slice(at, at + (match ?? '').length)}</span
							>{phone.slice(at + (match ?? '').length)}{/if}</bdi
					>
				{/if}
			</span>
			{#if detail}
				<span class="line detail">{detail}</span>
			{/if}
		{:else if chosen}
			<span class="picked">
				<Icon name="check" size={18} />
				<span class="name one">{name}</span>
			</span>
		{:else}
			<span class="name">{name}</span>
		{/if}
	</button>
</li>

<style>
	.item {
		list-style: none;
		min-inline-size: 0;
	}

	.tile {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: stretch;
		inline-size: 100%;
		block-size: var(--name-h);
		min-inline-size: 0;
		padding: 0 var(--space-5);
		text-align: center;
		background: var(--surface-card);
		color: var(--text-strong);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-tile);
		font-family: var(--font-sans);
		cursor: pointer;
	}

	.tile.row {
		text-align: start;
	}

	.name {
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		overflow: hidden;
		min-inline-size: 0;
		font-size: var(--text-base);
		font-weight: 600;
		line-height: 1.3;
		overflow-wrap: break-word;
	}

	.picked {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: var(--space-2);
		min-inline-size: 0;
	}

	.name.one {
		display: block;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		font-weight: 700;
	}

	.first {
		display: flex;
		align-items: baseline;
		gap: var(--space-4);
		min-inline-size: 0;
	}

	.line {
		min-inline-size: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.rowName {
		flex: 1 1 auto;
		font-size: var(--text-base);
		font-weight: 600;
		line-height: 1.3;
	}

	.phone {
		flex: none;
		font-family: var(--font-mono);
		font-size: var(--text-sm);
		font-weight: 500;
		color: var(--text-muted);
	}

	.match {
		color: var(--text-strong);
		font-weight: 700;
	}

	.detail {
		font-size: var(--text-sm);
		line-height: 1.4;
		color: var(--text-body);
	}
</style>
